import React, { useState, useEffect } from "react";
import { usePaginated, useAsync } from "../hooks";
import { api } from "../api";
import {
  useToast,
  useConfirm,
  Btn,
  Card,
  Spinner,
  ApiError,
  Modal,
  Pages,
  Empty,
} from "../ui";
import RefundForm from "./RefundForm";
import RefundDetailPanel from "./RefundDetailPanel";
import QuickTaskModal from "../clients/QuickTaskModal";

import { BRAND } from "../brand";

// ─── STATUS CONFIG ────────────────────────────────────────────────────────────
const REFUND_STATUSES = ["all", "not_started", "in_progress", "completed"];
const REFUND_STATUS_COLORS = {
  not_started: "bg-slate-100 text-slate-500",
  in_progress: "bg-blue-100 text-blue-700",
  completed: "bg-emerald-100 text-emerald-700",
};
const REFUND_STATUS_LABELS = {
  not_started: "Not Started",
  in_progress: "In Progress",
  completed: "Completed",
};

// ─── REFUND TYPE CONFIG ───────────────────────────────────────────────────────
const REFUND_TYPES = [
  { value: "refund", label: "💰 Cash Refund" },
  { value: "kai_credit", label: `💳 ${BRAND.creditName}` },
];

// ─── CURRENCY OPTIONS — same as receipts ─────────────────────────────────────
const CURRENCIES = [
  { value: "USD", label: "USD – US Dollar" },
  { value: "NGN", label: "NGN – Nigerian Naira" },
  { value: "GBP", label: "GBP – British Pound" },
  { value: "EUR", label: "EUR – Euro" },
  { value: "AED", label: "AED – UAE Dirham" },
  { value: "GHS", label: "GHS – Ghanaian Cedi" },
  { value: "KES", label: "KES – Kenyan Shilling" },
  { value: "ZAR", label: "ZAR – South African Rand" },
  { value: "CAD", label: "CAD – Canadian Dollar" },
  { value: "AUD", label: "AUD – Australian Dollar" },
];

// ─── SUMMARY CURRENCIES — only USD + NGN on dashboard (like receipts) ────────
const SUMMARY_CURRENCIES = ["USD", "NGN"];

const fmtMoney = (n, cur = "USD") =>
  n != null
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: cur,
        minimumFractionDigits: 2,
      }).format(n)
    : "–";

const fmtD = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

// ─── REFUNDS TAB ─────────────────────────────────────────────────────────────
export function RefundsTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const {
    data: refunds,
    total,
    page,
    totalPages,
    loading,
    error,
    setPage,
    refetch,
  } = usePaginated("/crm/refunds");
  const { data: clients } = usePaginated("/crm/clients", 1000);
  const { data: receipts } = usePaginated("/crm/receipts", 1000);

   // ✅ Fetch summary from backend with currency parameter
  const { 
    data: summary, 
    refetch: refetchSummary,
    loading: summaryLoading 
  } = useAsync(() =>
    api.get(`/crm/refunds/summary?currency=${summaryCurrency}`)
  );

  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [summaryCurrency, setSummaryCurrency] = useState(BRAND.currency); // default from brand.js
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [sel, setSel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionSaving, setAS] = useState(false);
  const [refundTasks, setRefundTasks] = useState([]);

  // ✅ Filter refunds by selected currency for list display
  const shown = (refunds || []).filter((ref) => {
    const ms = filterStatus === "all" || ref.refundStatus === filterStatus;
    const mt = filterType === "all" || ref.refundType === filterType;
    const mc = ref.currency === summaryCurrency; // ✅ Filter by summary currency
    const mq =
      !search ||
      ref.refundReference?.toLowerCase().includes(search.toLowerCase()) ||
      ref.clientName?.toLowerCase().includes(search.toLowerCase());
    return ms && mt && mc && mq;
  });

  // ✅ Use backend summary data instead of frontend calculation
  const refundSummary = summary || {
    notStarted: 0,
    inProgress: 0,
    completed: 0,
    totalRefundAmount: 0,
    totalKaiCreditAmount: 0,
    closedCount: 0,
    openCount: 0,
  };

  const doCreate = async (form) => {
    setSaving(true);
    try {
      await api.post("/crm/refunds", form);
      toast("Refund created");
      setModal(null);
      refetch();
      refetchSummary();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const doEdit = async (form) => {
    setSaving(true);
    try {
      await api.patch(`/crm/refunds/${sel._id}`, form);
      toast("Refund updated");
      setModal(null);
      refetch();
      refetchSummary();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async (id) => {
    if (!(await confirm({ title: "Delete refund?", message: "This refund record will be permanently removed.", confirmText: "Delete refund" }))) return;
    try {
      await api.delete(`/crm/refunds/${id}`);
      toast("Deleted");
      refetch();
      refetchSummary();
      if (modal === "detail") setModal(null);
    } catch (e) {
      toast(e.message, "error");
    }
  };

  const doStatusChange = async (id, status) => {
    setAS(true);
    try {
      await api.patch(`/crm/refunds/${id}/status`, { refundStatus: status });
      toast(`Status updated to ${REFUND_STATUS_LABELS[status]}`);
      refetch();
      refetchSummary();
      if (sel?._id === id) {
        const updated = await api.get(`/crm/refunds/${id}`);
        setSel(updated);
      }
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setAS(false);
    }
  };

  const doSend = async (id, dto) => {
    setSaving(true);
    try {
      await api.post(`/crm/refunds/${id}/send`, dto);
      toast("Refund notification sent!");
      setModal("detail");
      refetch();
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (ref) => {
    try {
      const full = await api.get(`/crm/refunds/${ref._id}`);
      setSel(full);
      setModal("detail");
      api.get(`/crm/tasks/by-refund/${ref._id}`).then(setRefundTasks).catch(() => setRefundTasks([]));
    } catch (e) {
      toast(e.message, "error");
    }
  };

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800">Refunds</h1>
        <Btn variant="primary" onClick={() => setModal("create")}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 4v16m8-8H4" />
          </svg>
          New Refund
        </Btn>
      </div>

      {/* ── Currency Toggle + Summary cards ─────────────────────────────────── */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
            Summary in
          </span>
          <div className="flex gap-1">
            {SUMMARY_CURRENCIES.map((cur) => (
              <button
                key={cur}
                onClick={() => {
                  setSummaryCurrency(cur);
                  refetchSummary(); // ✅ Refetch summary when currency changes
                }}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all
                  ${
                    summaryCurrency === cur
                      ? "bg-[rgb(var(--brand-rgb))] text-white"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
              >
                {cur}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {[
            {
              label: "Not Started",
              val: refundSummary.notStarted,
              color: "bg-slate-400",
            },
            {
              label: "In Progress",
              val: refundSummary.inProgress,
              color: "bg-blue-500",
            },
            {
              label: "Completed",
              val: refundSummary.completed,
              color: "bg-emerald-500",
            },
            {
              label: "Refund Total",
              val: fmtMoney(refundSummary.totalRefundAmount, summaryCurrency),
              color: "bg-amber-500",
            },
            {
              label: BRAND.creditName,
              val: fmtMoney(refundSummary.totalKaiCreditAmount, summaryCurrency),
              color: "bg-[rgb(var(--brand-rgb))]",
            },
            {
              label: "Open",
              val: refundSummary.openCount,
              color: "bg-orange-400",
            },
            {
              label: "Closed",
              val: refundSummary.closedCount,
              color: "bg-slate-600",
            },
          ].map((s) => (
            <Card key={s.label} className="p-4 flex items-center gap-3">
              <div className={`w-2.5 h-8 rounded-full flex-shrink-0 ${s.color}`} />
              <div>
                <p className="text-base font-bold text-slate-800 leading-tight">
                  {s.val}
                </p>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">
                  {s.label}
                </p>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* ── Filters ── */}
      <Card className="p-4 space-y-3">
        <div className="relative">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#94a3b8"
            strokeWidth="2"
            className="absolute left-3 top-1/2 -translate-y-1/2"
          >
            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by reference or client name…"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-[rgb(var(--brand-rgb)/0.3)] focus:border-[rgb(var(--brand-rgb))]"
          />
        </div>

        <div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
            Status
          </p>
          <div className="flex gap-2 flex-wrap">
            {REFUND_STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize
                  ${
                    filterStatus === s
                      ? "bg-[rgb(var(--brand-rgb))] text-white"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
              >
                {REFUND_STATUS_LABELS[s] || s}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
            Type
          </p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setFilterType("all")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all
                ${
                  filterType === "all"
                    ? "bg-[rgb(var(--brand-rgb))] text-white"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
            >
              All Types
            </button>
            {REFUND_TYPES.map((t) => (
              <button
                key={t.value}
                onClick={() => setFilterType(t.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all
                  ${
                    filterType === t.value
                      ? "bg-[rgb(var(--brand-rgb))] text-white"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {(filterStatus !== "all" || filterType !== "all" || search) && (
          <div className="flex items-center gap-2 pt-1 flex-wrap">
            <span className="text-[11px] text-slate-400">Filtering by: </span>
            {filterStatus !== "all" && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[rgb(var(--brand-rgb)/0.1)] text-[rgb(var(--brand-rgb))] capitalize">
                {REFUND_STATUS_LABELS[filterStatus]}
              </span>
            )}
            {filterType !== "all" && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[rgb(var(--brand-rgb)/0.1)] text-[rgb(var(--brand-rgb))]">
                {REFUND_TYPES.find((t) => t.value === filterType)?.label ||
                  filterType}
              </span>
            )}
            {search && (
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                "{search}"
              </span>
            )}
            <button
              onClick={() => {
                setFilterStatus("all");
                setFilterType("all");
                setSearch("");
              }}
              className="text-[11px] text-slate-400 hover:text-rose-500 font-bold transition-colors ml-1"
            >
              ✕ Clear
            </button>
          </div>
        )}
      </Card>

      {error && <ApiError msg={error} onRetry={refetch} />}
      {loading && <Spinner full />}

      {!loading && !error && (
        <>
          {!shown.length ? (
            <Empty
              icon="receipt"
              title="No refunds found"
              sub={
                search || filterStatus !== "all" || filterType !== "all"
                  ? "Try adjusting your filters"
                  : "Create your first refund"
              }
              action={
                !search &&
                filterStatus === "all" &&
                filterType === "all" && (
                  <Btn variant="primary" onClick={() => setModal("create")}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M12 4v16m8-8H4" />
                    </svg>
                    New Refund
                  </Btn>
                )
              }
            />
          ) : (
            <div className="space-y-2">
              {shown.map((ref) => {
                const typeInfo = REFUND_TYPES.find(
                  (t) => t.value === ref.refundType
                );
                const hasCredit = ref.refundType === "kai_credit";
                return (
                  <Card
                    key={ref._id}
                    className="p-4 hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          ref.refundStatus === "completed"
                            ? "bg-emerald-500"
                            : ref.refundStatus === "in_progress"
                            ? "bg-blue-500"
                            : "bg-slate-300"
                        }`}
                      />

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                          <p className="text-sm font-bold text-slate-800">
                            {ref.refundReference}
                          </p>
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${
                              REFUND_STATUS_COLORS[ref.refundStatus] ||
                              "bg-slate-100 text-slate-500"
                            }`}
                          >
                            {REFUND_STATUS_LABELS[ref.refundStatus]}
                          </span>
                          {typeInfo && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-50 text-purple-600">
                              {typeInfo.label}
                            </span>
                          )}
                          {hasCredit && ref.kaiCreditApplied && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-600">
                              ✓ Credit Applied
                            </span>
                          )}
                          {ref.isClosed && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-500">
                              🔒 Closed
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
                          <span className="flex items-center gap-1">
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {ref.clientName}
                          </span>
                          {ref.destination && (
                            <span>→ {ref.destination}</span>
                          )}
                          {ref.refundRequestDate && (
                            <span>Requested: {fmtD(ref.refundRequestDate)}</span>
                          )}
                          {ref.transactionReference && (
                            <span className="font-mono">
                              {ref.transactionReference}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="text-right mr-2">
                        <p
                          className={`text-base font-bold ${
                            ref.refundType === "kai_credit"
                              ? "text-[rgb(var(--brand-rgb))]"
                              : "text-amber-600"
                          }`}
                        >
                          {fmtMoney(ref.refundAmount, ref.currency)}
                        </p>
                        {ref.originalAmount != null &&
                          ref.originalAmount !== ref.refundAmount && (
                            <p className="text-[10px] text-slate-400">
                              of {fmtMoney(ref.originalAmount, ref.currency)}
                            </p>
                          )}
                        <p className="text-[10px] text-slate-400">
                          {ref.currency || "USD"}
                        </p>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <Btn
                          variant="secondary"
                          size="sm"
                          onClick={() => openDetail(ref)}
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View
                        </Btn>
                        <Btn
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSel(ref);
                            setModal("edit");
                          }}
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </Btn>
                        <Btn
                          variant="danger"
                          size="sm"
                          onClick={() => doDelete(ref._id)}
                        >
                          <svg
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </Btn>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
          <Pages
            page={page}
            totalPages={totalPages}
            total={total}
            onPage={setPage}
            loading={loading}
          />
        </>
      )}

      {/* ── MODALS ── */}
      {modal === "create" && (
        <Modal title="New Refund" onClose={() => setModal(null)} wide>
          <RefundForm
            clients={clients || []}
            receipts={receipts || []}
            onSubmit={doCreate}
            onClose={() => setModal(null)}
            saving={saving}
          />
        </Modal>
      )}

      {modal === "edit" && sel && (
        <Modal
          title={`Edit ${sel.refundReference}`}
          onClose={() => setModal(null)}
          wide
        >
          <RefundForm
            initial={sel}
            clients={clients || []}
            receipts={receipts || []}
            onSubmit={doEdit}
            onClose={() => setModal(null)}
            saving={saving}
          />
        </Modal>
      )}

      {modal === "detail" && sel && (
        <div
          className="fixed inset-0 z-50 flex"
          style={{
            background: "rgba(15,23,42,.55)",
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            className="ml-auto w-full max-w-2xl bg-white h-full flex flex-col shadow-2xl"
            style={{ animation: "slideIn .25s ease" }}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
              <span className="text-xs text-slate-400 uppercase tracking-wider font-semibold">
                Refund Detail
              </span>
              <button
                onClick={() => setModal(null)}
                className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <RefundDetailPanel
                refund={sel}
                tasks={refundTasks}
                actionSaving={actionSaving}
                onClose={() => setModal(null)}
                onEdit={() => setModal("edit")}
                onSend={() => setModal("send")}
                onAddTask={() => setModal("task")}
                onStatusChange={(status) =>
                  doStatusChange(sel._id, status)
                }
              />
            </div>
          </div>
        </div>
      )}

      {modal === "task" && sel && (
        <QuickTaskModal
          refundId={sel._id}
          clientId={sel.client?._id || sel.client || undefined}
          clientName={`${sel.refundReference} · ${sel.clientName}`}
          onDone={() => {
            setModal("detail");
            api.get(`/crm/tasks/by-refund/${sel._id}`).then(setRefundTasks).catch(() => {});
          }}
          onClose={() => setModal("detail")}
        />
      )}
    </div>
  );
}